import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { sortBy, map, noop, head, get } from 'lodash';
import cn from 'classnames';
import { Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';

import { isMobile as isMobileQuery } from 'admin/utils/media-queries';
import type { IUser } from 'src/types';
import Text from 'admin/components/common/Text';
import { Icon } from 'admin/components/common/Icon';
import { TextField } from 'admin/components/common/Form';
import ScrollBox from 'admin/components/common/ScrollBox';
import t from 'utils/translate';
import { routes } from 'admin/constants';

import FloatingBox from './FloatingBox';
import css from './User.scss';

type Props = {
	isOpen: boolean;
	user: IUser;
	view: 'full' | 'minimal';
	onOrganizationClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onLogOutClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onCloseClick: () => void;
};

export const WORKSPACE_FILTER_ID = 'workspace-filter';

type UseListFilterOptions<T> = {
	getSearchText: (item: T) => string;
	isOpen: boolean;
	items: T[];
	threshold?: number;
};

function useListFilter<T>({ items, threshold = 10, isOpen, getSearchText }: UseListFilterOptions<T>) {
	const [filter, setFilter] = useState('');
	const showFilter = items.length > threshold;
	const filteredItems = useMemo(() => {
		const query = filter.trim().toLowerCase();

		if (!query) return items;

		return items.filter(item => getSearchText(item).toLowerCase().includes(query));
	}, [filter, getSearchText, items]);

	useEffect(() => {
		if (!isOpen) setFilter('');
	}, [isOpen]);

	return { filter, filteredItems, setFilter, showFilter };
}

const Menu = forwardRef<HTMLDivElement, Props>(({ user, onOrganizationClick, onLogOutClick, isOpen, view }, ref) => {
	const activeOrganizationId = user.organization?.id;
	const organizations = useMemo(() => sortBy(user.organizations ?? [], ['name']), [user.organizations]);
	const getWorkspaceSearchText = useCallback((org: (typeof organizations)[number]) => get(org, 'name', ''), []);
	const filter = useListFilter({ items: organizations, isOpen, getSearchText: getWorkspaceSearchText });
	const isFullView = view === 'full';
	const location = useLocation();
	const isMobile = isMobileQuery();

	if (!isOpen) return null;

	return (
		<FloatingBox className={css.userMenu} ref={ref}>
			{isFullView ? (
				<>
					<div className={cn(css.section, css.sectionHead)}>
						<Text size={isMobile ? 'subheading' : 'label'} compact>
							{user.member.name || user.member.email}
						</Text>
						<div className={css.closeBtn} />
					</div>
					<div className={css.section}>
						<Text
							className={css.title}
							weight="semibold"
							size="description"
							compact
							color={Text.color.blackDark}
							transform={Text.transform.uppercase}
						>
							{t('user.workspaces')}
						</Text>
						{filter.showFilter && (
							<TextField
								id={WORKSPACE_FILTER_ID}
								autoFocus
								autoComplete="off"
								className={css.workspaceFilter}
								onChange={e => filter.setFilter(e.target.value)}
								placeholder="Search..."
								suffix={<Icon type="search" width={16} color="var(--ra-color-black-mid)" />}
								value={filter.filter}
							/>
						)}
						<ScrollBox shadowHexColor="#7a7a7a" maxHeight="80vh">
							{map(filter.filteredItems, org => {
								const isPending = org?.organizationMembers?.status === 'pending';
								const name = (
									<Text
										className={css.userOrganizationName}
										size={isMobile ? 'paragraph' : 'label'}
										weight={activeOrganizationId === org.id ? 'semibold' : 'normal'}
										compact
									>
										{get(org, 'name')}
									</Text>
								);

								return (
									<button
										key={org.id}
										data-id={org.id}
										onClick={isPending ? noop : onOrganizationClick}
										type="button"
										className={cn(css.userOrganization, { [css.pending]: isPending })}
									>
										<Text size="label" weight="normal" compact>
											<span className={css.orgAvatar}>{head(get(org, 'name'))}</span>
										</Text>
										{isPending ? (
											<Tooltip
												destroyOnHidden
												placement="topRight"
												title={() => (
													<Text
														size="body"
														text="Invitation isn't accepted. Please check your email."
													/>
												)}
											>
												{name}
											</Tooltip>
										) : (
											name
										)}
									</button>
								);
							})}
						</ScrollBox>
					</div>
					<div className={css.section}>
						<div className={css.item}>
							{location.pathname === routes.PROFILE_PAGE ? (
								<Text size={isMobile ? Text.size.paragraph : Text.size.label} compact>
									{t('user.profile')}
								</Text>
							) : (
								<Link to={routes.PROFILE_PAGE}>
									<Text size={isMobile ? Text.size.paragraph : Text.size.label} compact>
										{t('user.profile')}
									</Text>
								</Link>
							)}
						</div>
						<div className={css.item}>
							<button type="button" onClick={onLogOutClick}>
								<Text size={isMobile ? Text.size.paragraph : Text.size.label} compact>
									{t('user.logout')}
								</Text>
							</button>
						</div>
					</div>
				</>
			) : (
				<>
					<div className={css.section}>
						<Text size={Text.size.label} compact>
							{user.member.name || user.member.email}
						</Text>
					</div>
					<div className={css.section}>
						<button className={css.logoutBtn} type="button" onClick={onLogOutClick}>
							<Text tag="span" size={Text.size.label} compact>
								{t('user.logout')}
							</Text>
						</button>
					</div>
				</>
			)}
		</FloatingBox>
	);
});

Menu.displayName = 'UserMenu';

export default Menu;
